/**
 * SEC-08b (SCRUM-394): "my privacy requests" in the personal area.
 *
 *  GET  client-api/account/privacy-requests/  -> the user's requests (also the
 *       ones support opened from an email), each with a timeline of steps.
 *  POST client-api/account/privacy-requests/  {kind, details} -> new request.
 *
 * Deletion has its own flow (app-privacy-controls), so it isn't offered here.
 */
import { Component, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiUrl } from '../../shared/models/apiurl.models';
import { SystemApp } from '../../constans/SystemConfigurations';
import { ToastService } from '../../shared/toast/toast.service';

interface TimelineEvent { at: string; code: string; label: string; detail?: string; }

interface MyPrivacyRequest {
  id: number;
  ref: string;
  kind: string;
  kind_label: string;
  status: string;
  status_label: string;
  is_open: boolean;
  received_at: string;
  deadline: string;
  extended: boolean;
  days_left: number | null;
  overdue: boolean;
  closed_at: string | null;
  details: string;
  rejection_reason: string;
  timeline: TimelineEvent[];
}

interface MyPrivacyResponse {
  results: MyPrivacyRequest[];
  kinds: { value: string; label: string }[];
  max_open: number;
  due_days: number;
}

const KIND_HINTS: Record<string, string> = {
  access: 'את כל המידע אפשר גם להוריד מיד בכפתור "הורדה" למעלה.',
  correction: 'כתבו איזה פרט שגוי ומה הערך הנכון. רוב הפרטים אפשר לתקן לבד בטופס הפרטים האישיים.',
  marketing_removal: 'נסיר אותך מכל הודעות הפרסום (SMS, אימייל, וואטסאפ). הודעות שירות על החשבון ימשיכו להגיע.',
  other: 'תארו את הבקשה בנושא המידע האישי שלך.'
};

const SUBMIT_ERRORS: Record<string, string> = {
  INVALID_KIND: 'יש לבחור סוג בקשה.',
  DETAILS_REQUIRED: 'יש לפרט את הבקשה.',
  TOO_LONG: 'הטקסט ארוך מדי (עד 2000 תווים).',
  TOO_MANY_OPEN: 'יש לך כבר 3 בקשות פתוחות. נא להמתין לתשובה עליהן.'
};

@Component({
  selector: 'app-privacy-requests',
  standalone: true,
  imports: [FormsModule],
  template: `
  <section class="pr-panel" aria-labelledby="pr-title" dir="rtl">
    <div class="pr-panel__head">
      <div>
        <h3 id="pr-title" class="pr-panel__title">הבקשות שלי בנושא פרטיות</h3>
        <p class="pr-panel__hint">
          בקשות לעיון במידע, לתיקון או להסרה מדיוור. לפי החוק נשיב תוך {{ data?.due_days || 30 }} יום,
          וכאן אפשר לעקוב אחרי כל שלב.
        </p>
      </div>
      @if (!formOpen && data) {
        <button type="button" class="pr-panel__btn pr-panel__btn--primary" (click)="openForm()">בקשה חדשה</button>
      }
    </div>

    @if (formOpen && data) {
      <form class="pr-form" (ngSubmit)="submit()">
        <label class="pr-form__field">
          <span>סוג הבקשה</span>
          <select name="kind" [(ngModel)]="kind" required>
            @for (k of data.kinds; track k.value) { <option [value]="k.value">{{ k.label }}</option> }
          </select>
        </label>
        <p class="pr-panel__hint">{{ kindHint }}</p>
        <label class="pr-form__field">
          <span>פרטי הבקשה{{ detailsRequired ? '' : ' (לא חובה)' }}</span>
          <textarea name="details" rows="4" maxlength="2000" [(ngModel)]="details"></textarea>
          <small class="pr-form__count">{{ details.length }}/2000</small>
        </label>
        <p class="pr-panel__hint">למחיקת החשבון יש להשתמש באפשרות "מחיקת החשבון" למעלה.</p>
        <div class="pr-form__actions">
          <button type="submit" class="pr-panel__btn pr-panel__btn--primary" [disabled]="sending">
            {{ sending ? 'שולח…' : 'שליחת הבקשה' }}</button>
          <button type="button" class="pr-panel__btn pr-panel__btn--ghost" (click)="formOpen = false">ביטול</button>
        </div>
        @if (formError) { <p class="pr-panel__error" role="alert">{{ formError }}</p> }
      </form>
    }

    @if (loading) {
      <div class="pr-skeleton" aria-hidden="true"><span></span><span></span></div>
    } @else if (loadError) {
      <div class="pr-panel__row">
        <span>לא הצלחנו לטעון את הבקשות.</span>
        <button type="button" class="pr-panel__btn" (click)="load()">נסו שוב</button>
      </div>
    } @else if (data && !data.results.length) {
      <p class="pr-panel__empty">עדיין לא נשלחו בקשות.</p>
    } @else if (data) {
      <ul class="pr-list">
        @for (r of data.results; track r.id) {
          <li class="pr-item" [class.pr-item--open]="expanded === r.id">
            <button type="button" class="pr-item__summary" (click)="toggle(r.id)"
                    [attr.aria-expanded]="expanded === r.id" [attr.aria-controls]="'pr-body-' + r.id">
              <span class="pr-item__main">
                <span class="pr-item__title"><bdi dir="ltr">{{ r.ref }}</bdi> · {{ r.kind_label }}</span>
                <span class="pr-item__meta">
                  התקבלה {{ date(r.received_at) }}
                  @if (r.is_open) {
                    · מענה עד {{ date(r.deadline) }}{{ r.extended ? ' (הוארך)' : '' }}
                  } @else if (r.closed_at) {
                    · נסגרה {{ date(r.closed_at) }}
                  }
                </span>
              </span>
              <span class="pr-item__side">
                <span class="pr-pill pr-pill--{{ r.status }}">{{ r.status_label }}</span>
                @if (r.is_open && r.days_left !== null) {
                  <span class="pr-item__left" [class.pr-item__left--late]="r.overdue">
                    {{ r.overdue ? 'המועד עבר – אנחנו מטפלים' : (r.days_left === 0 ? 'היום' : 'עוד ' + r.days_left + ' ימים') }}
                  </span>
                }
              </span>
              <svg class="pr-item__chev" aria-hidden="true" width="16" height="16" viewBox="0 0 24 24">
                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>

            @if (expanded === r.id) {
              <div class="pr-item__body" [id]="'pr-body-' + r.id">
                @if (r.is_open) {
                  <div class="pr-progress" role="img" [attr.aria-label]="'עברו ' + elapsed(r) + ' מתוך ' + total(r) + ' ימים'">
                    <span class="pr-progress__bar" [class.pr-progress__bar--late]="r.overdue"
                          [style.width.%]="progress(r)"></span>
                  </div>
                  <p class="pr-panel__hint">עברו {{ elapsed(r) }} ימים מתוך {{ total(r) }} עד מועד המענה.</p>
                }
                @if (r.details) {
                  <p class="pr-item__details">{{ r.details }}</p>
                }
                <ol class="pr-timeline">
                  @for (e of r.timeline; track $index) {
                    <li class="pr-timeline__step pr-timeline__step--{{ e.code }}">
                      <span class="pr-timeline__dot" aria-hidden="true"></span>
                      <div>
                        <strong>{{ e.label }}</strong>
                        <time class="pr-timeline__time" [attr.datetime]="e.at">{{ dateTime(e.at) }}</time>
                        @if (e.detail) { <p class="pr-timeline__detail">{{ e.detail }}</p> }
                      </div>
                    </li>
                  }
                  @if (r.is_open) {
                    <li class="pr-timeline__step pr-timeline__step--next">
                      <span class="pr-timeline__dot" aria-hidden="true"></span>
                      <div>
                        <strong>מענה עד {{ date(r.deadline) }}</strong>
                        <span class="pr-timeline__time">נעדכן אותך במייל</span>
                      </div>
                    </li>
                  }
                </ol>
              </div>
            }
          </li>
        }
      </ul>
    }
  </section>
  `,
  styles: [`
    .pr-panel { margin-top: 24px; padding: 18px 20px; background: var(--input-bg, #f8fafc);
      border: 1px solid var(--border, #e2e8f0); border-radius: var(--radius-md, 14px); text-align: right; }
    .pr-panel__head { display: flex; gap: 16px; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; }
    .pr-panel__title { margin: 0; color: var(--text-main, #1e293b); }
    .pr-panel__hint { margin: 4px 0 0; font-size: 13px; color: var(--text-muted, #64748b); max-width: 560px; }
    .pr-panel__empty { margin: 14px 0 0; font-size: 14px; color: var(--text-muted, #64748b); }
    .pr-panel__error { margin: 8px 0 0; color: #c62828; font-size: 14px; }
    .pr-panel__row { display: flex; gap: 12px; align-items: center; justify-content: space-between; margin-top: 14px; flex-wrap: wrap; }
    .pr-panel__btn { min-height: 40px; padding: 8px 16px; border-radius: var(--radius-md, 14px);
      border: 1px solid var(--border, #e2e8f0); background: var(--surface, #fff); cursor: pointer;
      white-space: nowrap; font: inherit; }
    .pr-panel__btn:disabled { opacity: .6; cursor: default; }
    .pr-panel__btn:focus-visible, .pr-item__summary:focus-visible { outline: 3px solid #93c5fd; outline-offset: 2px; }
    .pr-panel__btn--primary { background: var(--primary, #2563eb); border-color: var(--primary, #2563eb); color: #fff; }
    .pr-panel__btn--primary:hover:not(:disabled) { background: var(--primary-dark, #1d4ed8); }
    .pr-panel__btn--ghost { border-color: transparent; background: transparent; }

    .pr-form { display: grid; gap: 8px; margin-top: 14px; padding: 14px; background: #fff;
      border: 1px solid var(--border, #e2e8f0); border-radius: 12px; }
    .pr-form__field { display: grid; gap: 4px; font-size: 14px; font-weight: 600; }
    .pr-form__field select, .pr-form__field textarea { font: inherit; font-weight: 400; padding: 8px 10px;
      border: 1px solid var(--border, #e2e8f0); border-radius: 8px; background: #fff; max-width: 560px; }
    .pr-form__field select { min-height: 40px; max-width: 320px; }
    .pr-form__count { font-weight: 400; font-size: 12px; color: var(--text-muted, #64748b); }
    .pr-form__actions { display: flex; gap: 8px; flex-wrap: wrap; }

    .pr-skeleton { display: grid; gap: 8px; margin-top: 14px; }
    .pr-skeleton span { height: 56px; border-radius: 12px; background: linear-gradient(90deg, #eef2f7, #f8fafc, #eef2f7);
      background-size: 200% 100%; animation: pr-shimmer 1.2s infinite; }
    @keyframes pr-shimmer { to { background-position: -200% 0; } }

    .pr-list { list-style: none; margin: 14px 0 0; padding: 0; display: grid; gap: 8px; }
    .pr-item { background: #fff; border: 1px solid var(--border, #e2e8f0); border-radius: 12px; }
    .pr-item--open { border-color: #bfdbfe; }
    .pr-item__summary { width: 100%; display: flex; align-items: center; gap: 12px; padding: 12px 14px;
      background: transparent; border: 0; cursor: pointer; text-align: right; font: inherit; color: inherit;
      border-radius: 12px; min-height: 56px; }
    .pr-item__main { flex: 1; min-width: 0; display: grid; gap: 2px; }
    .pr-item__title { font-weight: 700; color: var(--text-main, #1e293b); }
    .pr-item__meta { font-size: 13px; color: var(--text-muted, #64748b); }
    .pr-item__side { display: grid; gap: 4px; justify-items: end; }
    .pr-item__left { font-size: 12px; color: var(--text-muted, #64748b); white-space: nowrap; }
    .pr-item__left--late { color: #b45309; }
    .pr-item__chev { flex: none; color: var(--text-muted, #64748b); transition: transform .2s; }
    .pr-item--open .pr-item__chev { transform: rotate(180deg); }
    .pr-item__body { padding: 0 14px 14px; border-top: 1px solid var(--border, #e2e8f0); }
    .pr-item__details { margin: 12px 0 0; padding: 10px 12px; background: var(--input-bg, #f8fafc);
      border-radius: 8px; font-size: 14px; white-space: pre-wrap; overflow-wrap: anywhere; }

    .pr-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; font-weight: 600;
      background: #eef2f7; color: #334155; white-space: nowrap; }
    .pr-pill--received { background: #e0f2fe; color: #075985; }
    .pr-pill--verifying { background: #fef3c7; color: #92400e; }
    .pr-pill--in_progress { background: #eff6ff; color: #1d4ed8; }
    .pr-pill--answered { background: #dcfce7; color: #166534; }
    .pr-pill--rejected { background: #fee2e2; color: #991b1b; }

    .pr-progress { margin-top: 12px; height: 6px; border-radius: 999px; background: #e2e8f0; overflow: hidden; }
    .pr-progress__bar { display: block; height: 100%; background: var(--primary, #2563eb); border-radius: inherit; }
    .pr-progress__bar--late { background: #d97706; }

    .pr-timeline { list-style: none; margin: 14px 0 0; padding: 0; }
    .pr-timeline__step { position: relative; display: grid; grid-template-columns: 14px 1fr; gap: 10px;
      padding-bottom: 14px; font-size: 14px; }
    .pr-timeline__step::before { content: ''; position: absolute; top: 14px; bottom: 0; right: 6px; width: 2px; background: #e2e8f0; }
    .pr-timeline__step:last-child { padding-bottom: 0; }
    .pr-timeline__step:last-child::before { display: none; }
    .pr-timeline__dot { width: 14px; height: 14px; margin-top: 3px; border-radius: 50%;
      background: var(--primary, #2563eb); box-shadow: 0 0 0 3px #eff6ff; }
    .pr-timeline__step--answered .pr-timeline__dot { background: #16a34a; box-shadow: 0 0 0 3px #dcfce7; }
    .pr-timeline__step--rejected .pr-timeline__dot { background: #dc2626; box-shadow: 0 0 0 3px #fee2e2; }
    .pr-timeline__step--extended .pr-timeline__dot, .pr-timeline__step--verifying .pr-timeline__dot {
      background: #d97706; box-shadow: 0 0 0 3px #fef3c7; }
    .pr-timeline__step--next .pr-timeline__dot { background: #fff; border: 2px dashed #94a3b8; box-shadow: none; }
    .pr-timeline__step--next strong { color: var(--text-muted, #64748b); font-weight: 600; }
    .pr-timeline__time { display: block; font-size: 12px; color: var(--text-muted, #64748b); }
    .pr-timeline__detail { margin: 4px 0 0; font-size: 13px; color: #334155; }

    @media (max-width: 640px) {
      .pr-panel { padding: 14px; }
      .pr-item__summary { flex-wrap: wrap; }
      .pr-item__side { justify-items: start; grid-auto-flow: column; align-items: center; }
    }
    @media (prefers-reduced-motion: reduce) {
      .pr-item__chev { transition: none; }
      .pr-skeleton span { animation: none; }
    }
  `]
})
export class PrivacyRequestsComponent implements OnInit {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(ApiUrl);
  private readonly toast = inject(ToastService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly url = `${SystemApp.base_url_api}/client-api/account/privacy-requests/`;

  data: MyPrivacyResponse | null = null;
  loading = true;
  loadError = false;
  expanded: number | null = null;

  formOpen = false;
  kind = 'correction';
  details = '';
  sending = false;
  formError = '';

  private get headers() {
    return new HttpHeaders({ Authorization: this.apiUrl.getJWTToken() });
  }

  get kindHint(): string {
    return KIND_HINTS[this.kind] || '';
  }

  get detailsRequired(): boolean {
    return this.kind === 'correction' || this.kind === 'other';
  }

  ngOnInit(): void {
    if (this.isBrowser) this.load();
  }

  async load() {
    this.loading = true;
    this.loadError = false;
    try {
      this.data = await firstValueFrom(this.http.get<MyPrivacyResponse>(this.url, { headers: this.headers }));
      if (this.expanded === null) {
        const firstOpen = this.data.results.find((r) => r.is_open);
        if (firstOpen) this.expanded = firstOpen.id;
      }
    } catch {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  openForm() {
    this.formOpen = true;
    this.formError = '';
    this.details = '';
    this.kind = this.data?.kinds.some((k) => k.value === 'correction') ? 'correction' : (this.data?.kinds[0]?.value || 'other');
  }

  async submit() {
    if (this.sending) return;
    this.formError = '';
    if (this.detailsRequired && this.details.trim().length < 5) {
      this.formError = SUBMIT_ERRORS['DETAILS_REQUIRED'];
      return;
    }
    this.sending = true;
    try {
      const row = await firstValueFrom(this.http.post<MyPrivacyRequest>(this.url,
        { kind: this.kind, details: this.details.trim() }, { headers: this.headers }));
      this.formOpen = false;
      this.toast.success(`הבקשה ${row.ref} התקבלה. נשיב עד ${this.date(row.deadline)}.`);
      this.expanded = row.id;
      await this.load();
    } catch (e: any) {
      const code = e?.error?.statusCode;
      this.formError = e?.status === 429 ? 'נשלחו יותר מדי בקשות היום. נסו שוב מחר.'
        : (SUBMIT_ERRORS[code] || 'השליחה נכשלה, נסו שוב.');
    } finally {
      this.sending = false;
    }
  }

  toggle(id: number) {
    this.expanded = this.expanded === id ? null : id;
  }

  date(value: string | null): string {
    return value ? new Date(value).toLocaleDateString('he-IL') : '';
  }

  dateTime(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    return `${d.toLocaleDateString('he-IL')} · ${d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}`;
  }

  total(r: MyPrivacyRequest): number {
    const ms = new Date(r.deadline).getTime() - new Date(r.received_at).getTime();
    return Math.max(1, Math.round(ms / 86400000));
  }

  elapsed(r: MyPrivacyRequest): number {
    const ms = Date.now() - new Date(r.received_at).getTime();
    return Math.max(0, Math.min(this.total(r), Math.floor(ms / 86400000)));
  }

  progress(r: MyPrivacyRequest): number {
    return r.overdue ? 100 : Math.round((this.elapsed(r) / this.total(r)) * 100);
  }
}
